import { Controller } from "@hotwired/stimulus";

// Bridges the draggable destination marker on the
// delivery route map to the delivery order form.
//
// On every drag-end, the location_map controller
// emits `location-map:destinationDragged` with
// `{ latitude, longitude }` in the event detail.
// This controller writes those coordinates to two
// hidden inputs (so they ride along with the order
// submit) and re-fires the radius validation form
// so the user gets immediate feedback if they
// dragged outside the venue's delivery zone.
//
// When a different address is selected via
// autocomplete or saved-address picker, the pin that
// pick carries takes over (CAL-413); the override is
// only cleared when the pick has none, so that the
// next geocode result wins.
export default class extends Controller {
  static targets = ["latitude", "longitude", "badge"];
  static values = {
    validationFormId: { type: String, default: "" },
    addressLatitude: { type: String, default: "" },
    addressLongitude: { type: String, default: "" },
  };

  // The pin the CURRENT address arrives with: Google's
  // own coordinates for the place the customer picked,
  // or the ones they confirmed the first time they
  // saved it (CAL-413). Seeded here for the address the
  // page loaded with, replaced on every pick.
  //
  // "Reset to address" returns to it, so it must be the
  // address's pin and never the dragged one: nothing
  // in the drag path touches it.
  connect() {
    this.addressPin = this.seededAddressPin();
  }

  seededAddressPin() {
    const latitude = this.addressLatitudeValue;
    const longitude = this.addressLongitudeValue;
    if (!latitude || !longitude) return null;

    return { latitude, longitude };
  }

  pinDragged(event) {
    const { latitude, longitude } = event.detail;
    if (latitude == null || longitude == null) return;

    this.writeOverride(latitude, longitude);
    this.refireValidation(latitude, longitude);
    this.showBadge();
  }

  // The address changed under us: picked via
  // autocomplete or saved-address, or edited by hand
  // in the address fields (address-field-validator
  // dispatches `addressEdited` for that one, with no
  // location: a hand-edit never brings a pin).
  //
  // If the pick brought a pin with it (Google's own
  // coordinates for the place, or the ones stored on
  // a saved address), that pin IS the answer, and a
  // better one than geocoding the address text we
  // built from it (CAL-413). Keep it.
  //
  // We do NOT refire the validation here. The address
  // field validator answers this same event, submits
  // that form itself, and puts these coordinates on it
  // (see `setPinFields` there). A submit from here too
  // is a second request that aborts the first, and
  // the aborted one is the one that already reached
  // Rails and geocoded.
  //
  // The badge stays hidden either way: it reads
  // "you adjusted this", and the customer adjusted
  // nothing.
  //
  // Without a pin we drop the override so the
  // geocode takes effect on the next validation
  // round-trip. We MUST also drop the lat/lng from
  // the validation form: the validator blanks them
  // for the submit it is making right now, but a
  // later blur-triggered validation sends whatever is
  // still sitting on that form, and stale dragged
  // coordinates there would make the server skip the
  // geocode for an address they don't belong to.
  resetOverride(event) {
    const { latitude, longitude } =
      event?.detail?.location || {};

    if (latitude != null && longitude != null) {
      this.addressPin = { latitude, longitude };
      this.writeOverride(latitude, longitude);
      this.hideBadge();
      return;
    }

    // A pin belongs to the address that brought it, so
    // an address arriving without one leaves nothing to
    // reset TO. Forgetting it here is what stops the
    // badge's Reset from putting the PREVIOUS address's
    // point on this one.
    this.addressPin = null;
    this.writeOverride("", "");
    this.removeValidationOverride();
    this.hideBadge();
  }

  // The "Reset" link inside the badge: undo the drag
  // and put the map back on the address's own pin.
  //
  // That pin is Google's answer for the place the
  // customer picked, or the one they confirmed when
  // they saved the address, and it is a better
  // destination than anything a fresh geocode of the
  // text we assembled out of it returns, which is the
  // whole of CAL-413. We refire the validation with it
  // so the server re-checks the radius against that
  // point and the map snaps back to it.
  //
  // Only an address with no pin of its own (typed by
  // hand, or saved before we stored coordinates) falls
  // back to dropping the override and letting the
  // geocode decide, which is what this link did for
  // every address before.
  reset(event) {
    event?.preventDefault();

    const pin = this.addressPin;
    if (pin) {
      this.writeOverride(pin.latitude, pin.longitude);
      this.hideBadge();
      this.refireValidation(
        pin.latitude, pin.longitude
      );
      return;
    }

    this.resetOverride();
    const form = this.validationForm();
    if (form && this.hasMinimumFields(form)) {
      form.requestSubmit();
    }
  }

  // We toggle classList instead of the `hidden`
  // attribute because the badge uses `flex` for
  // layout, and Tailwind utility `display: flex`
  // beats UA `[hidden] { display: none }` by
  // specificity, leaving the badge visible.
  showBadge() {
    if (this.hasBadgeTarget) {
      this.badgeTarget.classList.remove("hidden");
      this.badgeTarget.classList.add("flex");
    }
  }

  hideBadge() {
    if (this.hasBadgeTarget) {
      this.badgeTarget.classList.remove("flex");
      this.badgeTarget.classList.add("hidden");
    }
  }

  validationForm() {
    if (!this.validationFormIdValue) return null;
    return document.getElementById(
      this.validationFormIdValue
    );
  }

  removeValidationOverride() {
    const form = this.validationForm();
    if (!form) return;

    ["latitude", "longitude"].forEach((field) => {
      const input = form.querySelector(
        `input[name="address_components[${field}]"]`
      );
      if (input) input.remove();
    });
  }

  writeOverride(latitude, longitude) {
    if (this.hasLatitudeTarget) {
      this.latitudeTarget.value = latitude;
    }
    if (this.hasLongitudeTarget) {
      this.longitudeTarget.value = longitude;
    }
  }

  refireValidation(latitude, longitude) {
    const form = this.validationForm();
    if (!form) return;

    this.setHiddenField(
      form, "address_components[latitude]",
      latitude
    );
    this.setHiddenField(
      form, "address_components[longitude]",
      longitude
    );

    // Don't fire if the address fields aren't
    // populated yet: the validator short-circuits
    // anyway, but skipping the request avoids
    // an unnecessary server round-trip.
    if (!this.hasMinimumFields(form)) return;

    form.requestSubmit();
  }

  setHiddenField(form, name, value) {
    let field = form.querySelector(
      `input[name="${name}"]`
    );
    if (!field) {
      field = document.createElement("input");
      field.type = "hidden";
      field.name = name;
      form.appendChild(field);
    }
    field.value = value;
  }

  hasMinimumFields(form) {
    const required = [
      "street_address", "city", "state", "country",
    ];
    return required.every((field) => {
      const input = form.querySelector(
        `input[name="address_components[${field}]"]`
      );
      return input && input.value.trim() !== "";
    });
  }
}
